/**
 * useCreatePost Hook
 *
 * Manages the workflow for creating new posts/drafts:
 * 1. Sends creation data (URL or manual entry) to n8n webhook
 * 2. n8n processes and creates a Blog Post with "Pending Approval" status
 * 3. Frontend polls until new Blog Post appears
 */

import { useState, useCallback, useRef } from "react";
import { fetchBlogPosts, checkUrlExists } from "api/supabase";
import { triggerNewPostCreation } from "api/webhook";

function useCreatePost(onSuccess) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const pollingIntervalRef = useRef(null);
  const creationStartTimeRef = useRef(null);

  /**
   * Check if a new Blog Post was created recently
   * @param {number} startTime - Timestamp when creation was initiated
   * @param {number} initialCount - Initial count of blog posts
   * @returns {Promise<{found: boolean, newPost: any}>} Result object
   */
  const checkNewBlogPostExists = useCallback(async (startTime, initialCount = 0) => {
    try {
      // Fetch Blog Posts with "Pending Approval" status
      const blogPosts = await fetchBlogPosts("Pending Approval");

      console.log(
        `[Polling] Checking for new blog post. Found: ${blogPosts.length} posts (initial: ${initialCount})`
      );

      // First check: Has the count increased?
      if (blogPosts.length > initialCount) {
        console.log("✅ New blog post detected: count increased");

        // Find the newest post
        const sortedPosts = blogPosts.sort((a, b) => {
          const dateA = new Date(a.created_at || a.date).getTime();
          const dateB = new Date(b.created_at || b.date).getTime();
          return dateB - dateA; // Newest first
        });

        const newPost = sortedPosts[0];
        const postCreatedTime = new Date(newPost.created_at || newPost.date).getTime();

        console.log(
          "✅ Found new blog post:",
          newPost.id,
          "Title:",
          newPost.title,
          "Created:",
          new Date(postCreatedTime).toISOString()
        );

        return { found: true, newPost };
      }

      // Second check: Check if any post was created after start time (with 30 second buffer)
      const bufferTime = startTime - 30000; // 30 seconds before to catch edge cases
      const newPost = blogPosts.find((post) => {
        const postCreatedTime = new Date(post.created_at || post.date).getTime();
        return postCreatedTime >= bufferTime;
      });

      if (newPost) {
        console.log("✅ Found new blog post via timestamp:", newPost.id, "Title:", newPost.title);
        return { found: true, newPost };
      }

      console.log("❌ No new blog post found. Start time:", new Date(startTime).toISOString());
      return { found: false, newPost: null };
    } catch (error) {
      console.error("Error checking for new Blog Post:", error);
      return { found: false, newPost: null };
    }
  }, []);

  /**
   * Start polling for new Blog Post creation
   * @param {number} startTime - Timestamp when creation was initiated
   * @param {number} initialCount - Initial count of blog posts
   */
  const startPolling = useCallback(
    (startTime, initialCount) => {
      console.log(
        `[Polling] Starting poll for new Blog Post. Start time: ${new Date(
          startTime
        ).toISOString()}, Initial count: ${initialCount}`
      );

      let pollCount = 0;
      const maxPolls = 120; // 120 polls * 5 seconds = 10 minutes

      const pollInterval = setInterval(async () => {
        pollCount++;
        console.log(`[Polling] Poll #${pollCount}/${maxPolls}`);

        const result = await checkNewBlogPostExists(startTime, initialCount);

        if (result.found) {
          console.log("✅ [Polling] New Blog Post created successfully!");

          // Stop polling
          clearInterval(pollInterval);
          pollingIntervalRef.current = null;

          // Update state
          setLoading(false);
          setSuccess(true);

          // Trigger success callback if provided
          if (onSuccess) {
            onSuccess();
          }
        } else if (pollCount >= maxPolls) {
          // Timeout reached
          console.log("⏰ [Polling] Timeout reached after", maxPolls, "polls");
          clearInterval(pollInterval);
          pollingIntervalRef.current = null;

          setLoading(false);
          setError(
            "Processing timeout. Your post may still be processing. Please check the Drafts tab manually."
          );
        }
      }, 5000); // Poll every 5 seconds (reduced from 10 seconds)

      // Store the interval
      pollingIntervalRef.current = pollInterval;
    },
    [checkNewBlogPostExists, onSuccess]
  );

  /**
   * Stop polling (cleanup)
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Create a new post from URL or manual entry
   *
   * Workflow:
   * 1. Get initial Blog Post count
   * 2. Trigger n8n webhook with creation data
   * 3. Start polling for new Blog Post creation
   * 4. Show loading state until Blog Post appears
   *
   * @param {Object} creationData - Post creation data
   * @param {string} creationData.type - "url" or "manual"
   * @param {string} [creationData.url] - URL to scrape (if type is "url")
   * @param {string} [creationData.sourceType] - Source type (if type is "url")
   * @param {string} [creationData.topic] - Topic/title (if type is "manual")
   * @param {string} [creationData.content] - Content (if type is "manual")
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  const createPost = useCallback(
    async (creationData) => {
      setError(null);
      setSuccess(false);
      setLoading(true);

      try {
        console.log("🚀 [Create Post] Starting post creation:", creationData);

        // Check for duplicate URL before sending to webhook
        if (creationData.type === "url" && creationData.url) {
          try {
            console.log("🔍 [Create Post] Checking for duplicate URL:", creationData.url);
            const { exists, record } = await checkUrlExists(creationData.url);

            if (exists) {
              console.warn("⚠️ [Create Post] URL already exists:", creationData.url);
              setLoading(false);
              setError(
                "This URL has already been processed. Please check the Drafts or Posted tabs for existing content."
              );
              return {
                success: false,
                error: "Duplicate URL detected",
              };
            }

            console.log("✅ [Create Post] URL is unique, proceeding...");
          } catch (checkError) {
            // Log the error but continue - we don't want to block creation if the check fails
            console.warn("⚠️ [Create Post] Could not check for duplicate URL:", checkError);
          }
        }

        // Get initial Blog Post count before creation
        let initialCount = 0;
        try {
          const existingPosts = await fetchBlogPosts("Pending Approval");
          initialCount = existingPosts.length;
          console.log(`📊 [Create Post] Initial blog post count: ${initialCount}`);
        } catch (err) {
          console.warn("Could not fetch initial blog post count:", err);
        }

        // Record start time for polling (with small buffer before)
        const startTime = Date.now() - 5000; // 5 seconds buffer to catch edge cases
        creationStartTimeRef.current = startTime;

        console.log(`⏰ [Create Post] Start time set to: ${new Date(startTime).toISOString()}`);

        // Start polling immediately with initial count
        startPolling(startTime, initialCount);

        // Trigger n8n webhook asynchronously
        triggerNewPostCreation(creationData)
          .then((result) => {
            if (result.success) {
              console.log("✅ [Create Post] Webhook triggered successfully:", result);
            } else {
              // Check if it's a timeout error - if so, continue polling
              if (result.isTimeout) {
                console.log(
                  "⚠️ [Create Post] Webhook timed out but polling continues (workflow still processing)"
                );
                // Don't stop polling on timeout - the workflow continues in n8n
              } else {
                // Non-timeout error - stop polling and show error
                console.error("❌ [Create Post] Webhook failed:", result.error);
                stopPolling();
                setLoading(false);
                setError(result.error || "Failed to trigger post creation workflow");
              }
            }
          })
          .catch((err) => {
            console.error("❌ [Create Post] Webhook error:", err);
            // Only show error if it's not a timeout (webhooks can timeout but workflow continues)
            if (!err.message.includes("timed out") && !err.message.includes("timeout")) {
              stopPolling();
              setLoading(false);
              setError(err.message);
            } else {
              console.log(
                "⚠️ [Create Post] Webhook timed out but polling continues (workflow may still complete)"
              );
            }
          });

        return {
          success: true,
          message: "Processing... Your draft will appear in the Drafts tab when ready.",
        };
      } catch (err) {
        console.error("❌ [Create Post] Error in post creation workflow:", err);
        setLoading(false);
        setError(err.message);

        return { success: false, error: err.message };
      }
    },
    [startPolling, stopPolling]
  );

  /**
   * Reset state
   */
  const resetState = useCallback(() => {
    setError(null);
    setSuccess(false);
    setLoading(false);
  }, []);

  /**
   * Cleanup on unmount
   */
  const cleanup = useCallback(() => {
    stopPolling();
  }, [stopPolling]);

  return {
    createPost,
    loading,
    error,
    success,
    resetState,
    cleanup,
  };
}

export default useCreatePost;
